/** App — root component with routing. */

import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/auth/AuthProvider";
import { AccountAuth as RequireAuth, RoleRoute } from "@/auth/RoleRoute";
import { hasRoleModeMarker, useRoleMode } from "@/auth/roleMode";
import { useJudgeSession } from "@/stores/judgeSession";
import { RoleAccessPage } from "@/pages/RoleAccess";
import { RoleWorkspacePage } from "@/pages/RoleWorkspace";
import { RunDayPage } from "@/pages/RunDay";
import { DeskRoute } from "@/pages/RoleDesk";
import { AnnouncerRoute } from "@/pages/Announcer";
import { UIInfrastructure } from "@/components/UIInfrastructure";
import { LandingPage } from "@/pages/Landing";
import { PricingPage } from "@/pages/Pricing";
import { useAuthStore } from "@/stores/auth";
import { lazy, Suspense, type ReactNode } from "react";

const AppShell = lazy(() =>
  import("@/components/layout/AppShell").then((module) => ({ default: module.AppShell })),
);
const LoginPage = lazy(() =>
  import("@/pages/Login").then((module) => ({ default: module.LoginPage })),
);
const DashboardPage = lazy(() =>
  import("@/pages/Dashboard").then((module) => ({ default: module.DashboardPage })),
);
const CompetitionViewPage = lazy(() =>
  import("@/pages/CompetitionView").then((module) => ({ default: module.CompetitionViewPage })),
);
const SetupPage = lazy(() =>
  import("@/pages/Setup").then((module) => ({ default: module.SetupPage })),
);
const ProducerPage = lazy(() =>
  import("@/pages/Producer").then((module) => ({ default: module.ProducerPage })),
);
const HeadJudgePage = lazy(() =>
  import("@/pages/HeadJudge").then((module) => ({ default: module.HeadJudgePage })),
);
const TieResolutionPage = lazy(() =>
  import("@/pages/TieResolution").then((module) => ({ default: module.TieResolutionPage })),
);
const JudgePage = lazy(() =>
  import("@/pages/Judge").then((module) => ({ default: module.JudgePage })),
);
const RidersPage = lazy(() =>
  import("@/pages/Riders").then((module) => ({ default: module.RidersPage })),
);
const UpgradePage = lazy(() =>
  import("@/pages/Upgrade").then((module) => ({ default: module.UpgradePage })),
);
const OBSOverlayPage = lazy(() =>
  import("@/pages/OBSOverlay").then((module) => ({ default: module.OBSOverlayPage })),
);
const InviteAcceptPage = lazy(() =>
  import("@/pages/InviteAccept").then((module) => ({ default: module.InviteAcceptPage })),
);
const QuizPage = lazy(() =>
  import("@/pages/QuizPage").then((module) => ({ default: module.QuizPage })),
);
const TrainingPage = lazy(() =>
  import("@/pages/Training").then((module) => ({ default: module.TrainingPage })),
);
const CoursePage = lazy(() =>
  import("@/pages/Training").then((module) => ({ default: module.CoursePage })),
);

const PublicResultsPage = lazy(() =>
  import("@/pages/PublicResults").then((module) => ({ default: module.PublicResultsPage })),
);
const AthleteProfilePage = lazy(() =>
  import("@/pages/AthleteProfile").then((module) => ({ default: module.AthleteProfilePage })),
);
const DemoPage = lazy(() =>
  import("@/pages/Demo").then((module) => ({ default: module.DemoPage })),
);
const PublicLegalPage = lazy(() =>
  import("@/pages/PublicLegal").then((module) => ({ default: module.PublicLegalPage })),
);
const PrintQueuePage = lazy(() =>
  import("@/pages/PrintQueue").then((module) => ({ default: module.PrintQueuePage })),
);
const TVModePage = lazy(() => import("@/pages/TVMode"));

function RouteSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading page"
      style={{ padding: 24, display: "grid", gap: 16, maxWidth: 1100, margin: "0 auto" }}
    >
      <div style={{ height: 36, width: "45%", background: "#cbd5e1", borderRadius: 6 }} />
      {[0, 1, 2].map((row) => (
        <div key={row} style={{ height: 100, background: "#e2e8f0", borderRadius: 8 }} />
      ))}
    </div>
  );
}

/** If user is logged in, redirect to dashboard. Otherwise show children (landing page). */
export function AuthRedirect({ children }: { children: ReactNode }) {
  const { user, loading } = useAuthStore();
  if (loading) return <>{children}</>;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AuthenticationContext({ children }: { children: ReactNode }) {
  const location = useLocation();
  const mode = useRoleMode((state) => state.active);
  const session = useJudgeSession((state) => state.session);
  return mode || session || hasRoleModeMarker() || location.pathname === "/j" ? (
    <>{children}</>
  ) : (
    <AuthProvider>{children}</AuthProvider>
  );
}
function AccessRoute() {
  const location = useLocation();
  return <RoleAccessPage key={location.key + location.hash} />;
}
export function App() {
  return (
    <BrowserRouter>
      <UIInfrastructure />
      <AuthenticationContext>
        <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/"
              element={
                <AuthRedirect>
                  <LandingPage />
                </AuthRedirect>
              }
            />
            <Route path="/results/demo" element={<DemoPage />} />
            <Route path="/results/:slug" element={<PublicResultsPage />} />
            <Route path="/results/:slug/announcer" element={<AnnouncerRoute />} />
            <Route path="/athlete/:athleteKey" element={<AthleteProfilePage />} />
            <Route path="/tv/:slug" element={<TVModePage />} />
            <Route path="/terms" element={<PublicLegalPage document="terms" />} />
            <Route path="/privacy" element={<PublicLegalPage document="privacy" />} />
            <Route path="/refunds" element={<PublicLegalPage document="refunds" />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/j" element={<AccessRoute />} />
            <Route path="/competition/:competitionId/workspace" element={<RoleWorkspacePage />} />
            <Route
              path="/competition/:competitionId/run"
              element={
                <RoleRoute roles={["starter", "head_judge", "chief_judge"]}>
                  {(context) => (
                    <RunDayPage
                      key={context.principalKey}
                      transport={context.transport}
                      principalKey={context.principalKey}
                      onSignOut={context.onSignOut}
                    />
                  )}
                </RoleRoute>
              }
            />
            <Route
              path="/competition/:competitionId/headjudge"
              element={
                <RoleRoute roles={["admin", "head_judge", "chief_judge", "chief_scorer"]}>
                  {(context) => (
                    <HeadJudgePage
                      key={context.principalKey}
                      transport={context.transport}
                      principalKey={context.principalKey}
                      getToken={context.getToken}
                      onSignOut={context.onSignOut}
                    />
                  )}
                </RoleRoute>
              }
            />
            <Route
              path="/competition/:competitionId/ties/:scope/:scopeId"
              element={
                <RoleRoute roles={["admin", "head_judge", "chief_judge", "starter"]}>
                  {(context) => (
                    <TieResolutionPage
                      key={context.principalKey}
                      transport={context.transport}
                      principalKey={context.principalKey}
                    />
                  )}
                </RoleRoute>
              }
            />
            <Route path="/competition/:competitionId/desk" element={<DeskRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/obs/:token" element={<OBSOverlayPage />} />
            <Route path="/invite/:token" element={<InviteAcceptPage />} />

            {/* Authenticated full-screen routes (no AppShell) */}
            <Route
              path="/quiz/:courseType"
              element={
                <RequireAuth>
                  <QuizPage />
                </RequireAuth>
              }
            />

            <Route
              path="/competition/:competitionId/producer"
              element={
                <RequireAuth>
                  <ProducerPage />
                </RequireAuth>
              }
            />
            <Route
              path="/competition/:competitionId/judge"
              element={
                <RoleRoute roles={["judge", "head_judge", "chief_judge"]} watchSession={false}>
                  {(context) => (
                    <JudgePage
                      key={context.principalKey}
                      transport={context.transport}
                      principalKey={context.principalKey}
                      queuePrincipalKey={context.session?.queuePrincipalKey}
                      getToken={context.getToken}
                      onSignOut={context.onSignOut}
                      onSessionInvalid={context.session ? context.onSessionInvalid : undefined}
                      workspacePath={
                        context.session?.roles.includes("head_judge")
                          ? `/competition/${context.session.competitionId}/workspace?role=link`
                          : undefined
                      }
                    />
                  )}
                </RoleRoute>
              }
            />

            <Route
              path="/competition/:competitionId/print-queue"
              element={
                <RequireAuth>
                  <PrintQueuePage />
                </RequireAuth>
              }
            />

            {/* Authenticated routes */}
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/competition/:competitionId" element={<CompetitionViewPage />} />
              <Route path="/competition/:competitionId/setup" element={<SetupPage />} />
              <Route path="/riders" element={<RidersPage />} />
              <Route path="/training" element={<TrainingPage />} />
              <Route path="/training/:courseType" element={<CoursePage />} />
              <Route path="/upgrade" element={<UpgradePage />} />
              <Route path="/support" element={<Navigate to="/training" replace />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthenticationContext>
    </BrowserRouter>
  );
}
